/**
 * 認證相關 API 服務
 */

import apiClient, {
  setViewerToken,
  setMemberToken,
  setUserInfo,
  getUserInfo,
  getStorage,
  clearAllTokens,
  STORAGE_KEYS,
  SYSTEM_TOKEN
} from '@shared/api/client';
import { ENDPOINTS } from '@shared/api/endpoints';

let _viewerTokenPromise = null;

/**
 * 取得 Viewer Token (綁定裝置)
 * APP 啟動時自動呼叫
 */
export const getViewerToken = async () => {
  const existingToken = getStorage(STORAGE_KEYS.VIEWER_TOKEN);
  if (existingToken) return existingToken;

  // 若已有進行中的請求，共用同一個 promise，避免同時發出多次 API
  if (_viewerTokenPromise) return _viewerTokenPromise;

  _viewerTokenPromise = apiClient.get(ENDPOINTS.AUTH.VIEWER_TOKEN, {
    headers: { 'X-Auth-Token': SYSTEM_TOKEN }
  }).then((response) => {
    const viewerToken = response.token;
    if (!viewerToken) throw new Error('No token received from API');
    setViewerToken(viewerToken);
    return viewerToken;
  }).catch((error) => {
    console.error('❌ Failed to get Viewer Token:', error);
    throw error;
  }).finally(() => {
    _viewerTokenPromise = null;
  });

  return _viewerTokenPromise;
};

export const register = async (userData) => {
  try {
    const viewerToken = getStorage(STORAGE_KEYS.VIEWER_TOKEN);
    const response = await apiClient.post(ENDPOINTS.AUTH.REGISTER, {
      user_name: userData.name,
      dharma_name: userData.dharmaName || null,
      monastery_id: userData.jingsheId,
      birthday: userData.birthday,
      reward_id: userData.rewardId || null
    }, {
      headers: { 'X-Auth-Token': viewerToken }
    }) 

    setMemberToken(response.token);

    const userInfo = {
      name: response.user_name,
      dharmaName: response.dharma_name,
      monasteryId: response.monastery_id,
      monasteryName: response.monastery_name,
      userId: response.user_id,
      avatar: response.mascot_url || null,
    };
    setUserInfo(userInfo);

    window.dispatchEvent(new Event('auth-change'));

    return { token: response.token, user: userInfo, encouragement: response.encouragement };
  } catch (error) {
    console.error('❌ Registration failed:', error);
    throw error;
  }
};

export const login = async (credentials) => {
  try {
    const response = await apiClient.post(ENDPOINTS.AUTH.LOGIN, {
      user_name: credentials.name,
      dharma_name: credentials.dharmaName || null,
      monastery_id: credentials.jingsheId,
      birthday: credentials.birthday
    }, {
      headers: { 'X-Auth-Token': SYSTEM_TOKEN }
    });

    setMemberToken(response.token);

    const userInfo = {
      name: response.user_name,
      dharmaName: response.dharma_name,
      monasteryId: response.monastery_id,
      monasteryName: response.monastery_name,
      userId: response.user_id,
      avatar: response.mascot_url || null,
    };
    setUserInfo(userInfo);

    window.dispatchEvent(new Event('auth-change'));

    return { token: response.token, user: userInfo };
  } catch (error) {
    console.error('❌ Login failed:', error);
    throw error;
  }
};

/**
 * 用戶登出
 */
export const logout = async () => {
  clearAllTokens();
  window.dispatchEvent(new Event('auth-change'));
  // 登出後重新取得 viewer token，避免後續 API 無 token 可用
  await getViewerToken();
};

/**
 * 檢查是否已登入（有 Member Token）
 */
export const isAuthenticated = () => {
  return !!getStorage(STORAGE_KEYS.MEMBER_TOKEN);
};

export const getCurrentUser = () => {
  return getUserInfo();
};

/**
 * 檢查會員通知
 * 未登入帶 viewer token，已登入帶 member token（由 apiClient interceptor 自動處理）
 * @returns {{ show_notification: boolean, notification: string }}
 */
export const checkMemberNotification = async () => {
  try {
    const response = await apiClient.get(ENDPOINTS.AUTH.MEMBER_CHECK);

    // Refresh user's totalPoint in localStorage if available
    if (response.total_point !== undefined) {
      const currentUser = getUserInfo();
      if (currentUser && currentUser.totalPoint !== response.total_point) {
        setUserInfo({
          ...currentUser,
          totalPoint: response.total_point
        });
        window.dispatchEvent(new Event('auth-change'));
      }
    }

    return {
      show_notification: response.show_notification ?? false,
      notification: response.notification ?? '',
      total_point: response.total_point,
    };
  } catch (error) {
    console.error('❌ checkMemberNotification failed:', error);
    return { show_notification: false, notification: '' };
  }
};
